import type { Dispatch, JSX, ReactNode, SetStateAction } from 'react';
import { createContext, useMemo, useState } from 'react';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';

type SplitViewContextValue = {
    isSplitViewAvailable: boolean;
    isSplitViewOpen: boolean;
    isSplitViewOpenWhenAvailable: boolean;
    resetSplitView: () => void;
    setIsSplitViewAvailable: Dispatch<SetStateAction<boolean>>;
    setIsSplitViewOpenWhenAvailable: Dispatch<SetStateAction<boolean>>;
    setSplitViewContent: Dispatch<SetStateAction<JSX.Element | null>>;
    splitViewContent: JSX.Element | null;
};

const SplitViewContext = createContext<SplitViewContextValue | null>(null);

function useSplitViewContext(): SplitViewContextValue {
    return useDefinedContext(SplitViewContext);
}

function SplitViewProvider({ children }: { readonly children: ReactNode }): JSX.Element {
    const isSplitViewAvailableInitial = false;
    const isSplitViewOpenWhenAvailableInitial = true;
    const splitViewContentInitial = null;
    const [isSplitViewAvailable, setIsSplitViewAvailable] = useState(isSplitViewAvailableInitial);
    const [isSplitViewOpenWhenAvailable, setIsSplitViewOpenWhenAvailable] = useState(
        isSplitViewOpenWhenAvailableInitial
    );
    const [splitViewContent, setSplitViewContent] = useState<JSX.Element | null>(splitViewContentInitial);
    const isSplitViewOpen = isSplitViewOpenWhenAvailable && isSplitViewAvailable && Boolean(splitViewContent);

    function resetSplitView(): void {
        setIsSplitViewAvailable(isSplitViewAvailableInitial);
        setIsSplitViewOpenWhenAvailable(isSplitViewOpenWhenAvailableInitial);
        setSplitViewContent(splitViewContentInitial);
    }

    const contextValue = useMemo(
        () => ({
            isSplitViewAvailable,
            isSplitViewOpen,
            isSplitViewOpenWhenAvailable,
            resetSplitView,
            setIsSplitViewAvailable,
            setIsSplitViewOpenWhenAvailable,
            setSplitViewContent,
            splitViewContent,
        }),
        [isSplitViewAvailable, isSplitViewOpen, isSplitViewOpenWhenAvailable, splitViewContent]
    );

    return <SplitViewContext.Provider value={contextValue}>{children}</SplitViewContext.Provider>;
}

export { SplitViewProvider, useSplitViewContext };
